import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Box, Chip, Typography } from '@mui/material';
import { type GridRowId, type GridRowsProp , type GridColDef } from '@mui/x-data-grid';
import { DateTime } from 'luxon';
import { type FC, useMemo } from 'react';

import { useCheckPermissions } from '../../auth/index.js';
import { type Log, type Patient } from '../../models/index.js';
import { capitalize } from '../../utils/index.js';
import Table from '../Table/Table.js';
import tableActions from '../Table/tableActions.js';

interface ParsedLog extends Log {
  dateTime: DateTime;
}

interface LogsTableProps {
  data?: Patient & { logs: Log[] };
  isLoading: boolean;
  onEdit: (id: GridRowId) => () => void;
  onDelete: (id: GridRowId) => () => void;
}
const LogsTable: FC<LogsTableProps> = ({ data = {}, isLoading, onEdit, onDelete }) => {
  const { timeZone, logs = [] } = data;

  const { canManageLog } = useCheckPermissions();

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      { field: 'date', headerName: 'Date', flex: 1 },
      { field: 'time', headerName: 'Time', width: 100 },
      { field: 'duration', headerName: 'Duration', width: 100 },
      { field: 'code', headerName: 'Code', width: 100 },
      { field: 'description', headerName: 'Description', flex: 1 },
      {
        field: 'source',
        headerName: 'Source',
        width: 90,
        renderCell: ({ value }) => value === 'ai' ? (
          <Chip
            size={'small'}
            variant={'outlined'}
            color={'primary'}
            icon={<AutoAwesomeIcon />}
            label={'AI'}
          />
        ) : null,
      },
      {
        field: 'metadata',
        headerName: 'Additional information',
        flex: 1,
      },
    ];
    if (canManageLog) {
      cols.push({
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        type: 'actions',
        getActions: (params) => tableActions(onDelete(params.id), onEdit(params.id))({
          canEdit: canManageLog,
          canDelete: canManageLog,
        }),
      });
    }
    return cols;
  }, [onEdit, onDelete, canManageLog]);

  const rows: GridRowsProp = useMemo(() => {
    const parsedLogs: ParsedLog[] = logs.map((log: Log) => {
      return {
        ...log,
        dateTime: DateTime.fromISO(log.createdAt!, { zone: timeZone }),
      };
    });

    return parsedLogs.map(({ dateTime, code, metadata, id, activityEventId }, i: number) => {
      // Format datetimes with the Patient's time zone
      const previousLog = parsedLogs[i - 1];

      return {
        id,
        // https://github.com/moment/luxon/blob/master/docs/formatting.md
        date: dateTime.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY),
        time: dateTime.toLocaleString(DateTime.TIME_24_WITH_SECONDS),
        duration: previousLog ? dateTime.diff(previousLog.dateTime).toFormat('hh:mm:ss') : null,
        code: capitalize(code?.code || ''),
        description: capitalize(code?.description || 'N/A'),
        source: activityEventId ? 'ai' : 'user',
        metadata: metadata?.map(({ notes, codeMetadata }) => {
          let metadataContent = '';
          if (codeMetadata?.prompt) {
            metadataContent += `${capitalize(codeMetadata.prompt)}: `;
          }
          if (notes) {
            metadataContent += capitalize(notes);
          }
          return metadataContent;
        }),
      };
    });
  }, [logs, timeZone]);

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Table
        columns={columns}
        rows={rows}
        NoRowsOverlayText={'No logs available.'}
        isLoading={isLoading}
        pageSize={10}
        pageSizeOptions={[10, 25, 50, 100]}
        density={'compact'}
        disableColumnResize={false}
      />
      {timeZone ? (
        <Typography variant={'caption'}>
          All times shown in {timeZone} as 24-hour time. Times are formatted as hh:mm:ss.
        </Typography>
      ) : null}
    </Box>
  );
};

export default LogsTable;
